import { OrganizationType } from './Organization';

/** Legacy org-entity plan labels still returned on some Organization payloads. */
export enum Plan {
  BASIC = 'BASIC',
  PRO = 'PRO',
  PREMIUM = 'PREMIUM',
}

/** Legacy org-entity billing interval still returned on some Organization payloads. */
export enum PlanVariant {
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY',
}

export enum SubscriptionPlanCode {
  TUTOR_BASIC = 'TUTOR_BASIC',
  TUTOR_PRO = 'TUTOR_PRO',
  STUDIO_BASIC = 'STUDIO_BASIC',
  STUDIO_PRO = 'STUDIO_PRO',
  SCHOOL_BASIC = 'SCHOOL_BASIC',
  SCHOOL_MEDIUM = 'SCHOOL_MEDIUM',
  SCHOOL_PRO = 'SCHOOL_PRO',
  /** Private catalogue — only via organization plan offers. */
  STUDIO_EARLY_ADOPTER_4_PERCENT = 'STUDIO_EARLY_ADOPTER_4_PERCENT',
  STUDIO_EARLY_ADOPTER_5_PERCENT = 'STUDIO_EARLY_ADOPTER_5_PERCENT',
  STUDIO_EARLY_ADOPTER_7_PERCENT = 'STUDIO_EARLY_ADOPTER_7_PERCENT',
  STUDIO_EARLY_ADOPTER_8_PERCENT = 'STUDIO_EARLY_ADOPTER_8_PERCENT',
  STUDIO_EARLY_ADOPTER_250_PLN = 'STUDIO_EARLY_ADOPTER_250_PLN',
  STUDIO_EARLY_ADOPTER_FREE = 'STUDIO_EARLY_ADOPTER_FREE',
  SCHOOL_EARLY_ADOPTER_4_PERCENT = 'SCHOOL_EARLY_ADOPTER_4_PERCENT',
  SCHOOL_EARLY_ADOPTER_FREE = 'SCHOOL_EARLY_ADOPTER_FREE',
  TUTOR_EARLY_ADOPTER_FREE = 'TUTOR_EARLY_ADOPTER_FREE',
}

export enum SubscriptionTier {
  BASIC = 'BASIC',
  MEDIUM = 'MEDIUM',
  PRO = 'PRO',
}

export enum BillingInterval {
  MONTHLY = 'MONTHLY',
}

export enum SubscriptionStatus {
  INCOMPLETE = 'INCOMPLETE',
  INCOMPLETE_EXPIRED = 'INCOMPLETE_EXPIRED',
  TRIALING = 'TRIALING',
  ACTIVE = 'ACTIVE',
  PAST_DUE = 'PAST_DUE',
  CANCELED = 'CANCELED',
  UNPAID = 'UNPAID',
  PAUSED = 'PAUSED',
}

export interface SubscriptionPlanSummary {
  priceId: string;
  organizationType: OrganizationType;
  code: SubscriptionPlanCode;
  tier: SubscriptionTier;
  billingInterval: BillingInterval;
  amount: number;
  currencyCode: string;
  onlineBookingCommissionPercent: number;
  outsidePlatformBookingCommissionPercent: number;
}

/** Admin grant/update body for `POST …/subscription-plan-offers`. */
export interface CreateOrganizationSubscriptionPlanOfferRequest {
  subscriptionPlanPriceId: string;
  /** ISO date-time; null/omit for no expiry. Must be in the future when set. */
  expiresAt?: string | null;
  note?: string | null;
}

/** Admin offer row from `GET …/subscription-plan-offers`. */
export interface OrganizationSubscriptionPlanOffer {
  id: string;
  organizationId: string;
  /** Present when the API joins organization (list-by-price and org list). */
  organizationName?: string | null;
  plan: SubscriptionPlanSummary;
  expiresAt: string | null;
  note: string | null;
  createdAt: string;
}

/** Admin subscriber row from `GET …/subscription-plans/{priceId}/subscribers`. */
export interface OrganizationSubscriptionPlanSubscriber {
  organizationId: string;
  organizationName: string | null;
  status: SubscriptionStatus;
  trialEndsAt: string | null;
  currentPeriodEndsAt: string | null;
  cancelAtPeriodEnd: boolean;
}

/** True for private early-adopter catalogue codes (not self-service). */
export function isPrivateOfferPlanCode(
  code: SubscriptionPlanCode | string | null | undefined,
): boolean {
  return typeof code === 'string' && code.includes('EARLY_ADOPTER');
}

export interface OrganizationSubscriptionStatus {
  planCode: SubscriptionPlanCode | null;
  status: SubscriptionStatus | null;
  trialEndsAt: string | null;
  currentPeriodEndsAt: string | null;
  cancelAtPeriodEnd: boolean | null;
}

export interface CreateSubscriptionCheckoutRequest {
  /** Catalogue `priceId` from GET /plans. Do not send `targetOrganizationType` — backend ignores it. */
  subscriptionPlanPriceId: string;
}

export interface CreateSubscriptionPortalRequest {
  /** Catalogue `priceId` from GET /plans — opens portal confirm for that plan. */
  subscriptionPlanPriceId?: string;
}

/** Body for `POST /v1/host/subscription/change` (backend-controlled price switch). */
export interface ChangeSubscriptionPlanRequest {
  subscriptionPlanPriceId: string;
}

/**
 * Response from `POST /v1/host/subscription/change`.
 * Local plan/status update after Stripe webhook; typically `PENDING_WEBHOOK`.
 */
export interface SubscriptionPlanChangeResponse {
  subscriptionPlanPriceId: string;
  status: string;
}

export interface CheckoutSubscriptionResponse {
  stripeCheckoutSessionId: string;
}

export interface SubscriptionPortalResponse {
  url: string;
}

export enum SubscriptionInvoiceStatus {
  DRAFT = 'DRAFT',
  OPEN = 'OPEN',
  PAID = 'PAID',
  VOID = 'VOID',
  UNCOLLECTIBLE = 'UNCOLLECTIBLE',
}

export interface SubscriptionInvoice {
  id: string;
  /** Human-readable Stripe line/invoice summary; may be null. */
  description: string | null;
  number: string | null;
  status: SubscriptionInvoiceStatus;
  currencyCode: string;
  /** Stripe minor units (e.g. grosze). */
  amountDue: number;
  /** Stripe minor units (e.g. grosze). */
  amountPaid: number;
  createdAt: string;
  periodStart: string | null;
  periodEnd: string | null;
  hostedInvoiceUrl: string | null;
  invoicePdfUrl: string | null;
}

export interface SubscriptionInvoicePage {
  items: SubscriptionInvoice[];
  hasMore: boolean;
}

export interface GetSubscriptionInvoicesParams {
  limit?: number;
  startingAfter?: string;
}

/** Cash booking commission debt before / on the next subscription invoice. */
export type CashCommissionSourceType = 'ROOM_BOOKING' | 'TUTOR_BOOKING';

export type CashCommissionChargeStatus = 'PENDING_INVOICE' | 'INVOICED';

export interface CashCommissionCharge {
  id: string;
  sourceType: CashCommissionSourceType;
  sourceBookingId: string;
  /** Major currency units (e.g. 18.50 PLN), not Stripe minor units. */
  amount: number;
  currencyCode: string;
  status: CashCommissionChargeStatus;
  stripeInvoiceItemId: string | null;
  paymentCollectedAt: string;
}

export interface CashCommissionChargesResponse {
  items: CashCommissionCharge[];
  /** Sum of PENDING_INVOICE only — waiting to be accepted by Stripe as invoice items. */
  pendingAmount: number;
  currencyCode: string;
  /** Expected next subscription invoice date; null when no subscription period. */
  nextInvoiceAt: string | null;
}

/** Unified online + cash commission ledger (`GET …/commission-charges`). */
export type CommissionChannelFilter = 'ALL' | 'ONLINE' | 'CASH';

export type CommissionChargeChannel = 'ONLINE' | 'CASH';

export type CommissionSourceType = 'ROOM_BOOKING' | 'TUTOR_BOOKING';

/** Online + cash settlement lifecycle — do not derive client-side. */
export type CommissionSettlementStatus =
  | 'COLLECTED'
  | 'PARTIALLY_REVERSED'
  | 'REVERSED'
  | 'PENDING_INVOICE'
  | 'INVOICED'
  | 'VOIDED'
  | 'CREDIT_PENDING'
  | 'CREDITED';

export interface CommissionCharge {
  channel: CommissionChargeChannel;
  settlementStatus: CommissionSettlementStatus;
  sourceType: CommissionSourceType;
  sourceBookingId: string;
  /** Major units; null on older cash-ledger rows. */
  bookingAmount: number | null;
  /**
   * Actual Stripe processing fee (major units), from fee_details[type=stripe_fee].
   * Excludes platform commission. Null for cash, historical, or not-yet-confirmed
   * payments — treat as unavailable, not zero.
   */
  stripeProcessingFeeAmount?: number | null;
  /** Currency of the Stripe processing fee; may differ from `currencyCode`. */
  stripeProcessingFeeCurrency?: string | null;
  /**
   * Stripe-hosted receipt for the confirmed charge. Bearer-style URL — only
   * render as an authorized action. Null for cash/historical/unconfirmed.
   */
  stripeReceiptUrl?: string | null;
  /** Null on older cash-ledger rows. */
  commissionPercent: number | null;
  /** Original commission in major currency units — do not derive. */
  commissionAmount: number;
  /** Cancellation reversal in major units — do not derive. */
  commissionReversedAmount: number;
  /** Amount retained by the platform — do not derive from amount − reversed. */
  netCommissionAmount: number;
  currencyCode: string;
  /** Support/debug id — not a hosted Stripe URL. */
  stripeReferenceId: string | null;
  collectedAt: string;
}

export interface CommissionChargesResponse {
  items: CommissionCharge[];
  onlineCollectedAmount: number;
  cashPendingAmount: number;
  cashInvoicedAmount: number;
  currencyCode: string;
}

export interface GetCommissionChargesParams {
  channel?: CommissionChannelFilter;
}

/** Default Stripe payment method on the organization subscription customer. */
export interface SubscriptionPaymentMethod {
  id: string;
  /** Stripe payment-method type (e.g. `card`). */
  type: string;
  cardBrand: string | null;
  cardLast4: string | null;
  cardExpMonth: number | null;
  cardExpYear: number | null;
}

export interface SubscriptionPaymentMethodResponse {
  paymentMethod: SubscriptionPaymentMethod | null;
}

/** Format Stripe invoice amounts (minor units) for display. */
export function formatInvoiceAmount(
  amountMinor: number,
  currencyCode: string,
  locale?: string,
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: (currencyCode || 'PLN').toUpperCase(),
  }).format(amountMinor / 100);
}

/** Format commission amounts already in major currency units. */
export function formatCommissionAmount(
  amountMajor: number,
  currencyCode: string,
  locale?: string,
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: (currencyCode || 'PLN').toUpperCase(),
  }).format(amountMajor);
}

/** @deprecated Prefer `formatCommissionAmount`. */
export const formatCashCommissionAmount = formatCommissionAmount;

/** Title-case a Stripe card brand for display (`visa` → `Visa`). */
export function formatCardBrand(brand: string | null | undefined): string | null {
  if (!brand?.trim()) return null;
  const normalized = brand.trim().toLowerCase();
  if (normalized === 'amex') return 'Amex';
  if (normalized === 'diners') return 'Diners';
  if (normalized === 'mastercard') return 'Mastercard';
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

/**
 * Primary label for a subscription payment method.
 * Card example: `Visa •••• 4242`. Non-card types fall back to the Stripe `type`.
 */
export function formatSubscriptionPaymentMethodLabel(
  method: SubscriptionPaymentMethod,
): string {
  if (method.type === 'card' || method.cardLast4) {
    const brand = formatCardBrand(method.cardBrand) ?? 'Card';
    if (method.cardLast4) {
      return `${brand} •••• ${method.cardLast4}`;
    }
    return brand;
  }
  return method.type;
}

/** Expiry caption when month/year are present, e.g. `12/2030`. */
export function formatSubscriptionPaymentMethodExpiry(
  method: SubscriptionPaymentMethod,
): string | null {
  if (method.cardExpMonth == null || method.cardExpYear == null) {
    return null;
  }
  const month = String(method.cardExpMonth).padStart(2, '0');
  return `${month}/${method.cardExpYear}`;
}

/** True when selecting this plan would change an existing organization type. */
export function isOrganizationTypePlanTransition(
  currentType: OrganizationType | null | undefined,
  selectedPlanType: OrganizationType,
): boolean {
  return currentType != null && currentType !== selectedPlanType;
}

/**
 * Cross-type switches that need an in-app confirm before Stripe.
 * Leaving REHEARSAL_STUDIO for MUSIC_SCHOOL skips confirm — that path restores school
 * capabilities without the tutor-org tradeoffs. Studio → individual tutor still confirms.
 */
export function requiresOrganizationTypeChangeConfirm(
  currentType: OrganizationType | null | undefined,
  selectedPlanType: OrganizationType,
): boolean {
  if (!isOrganizationTypePlanTransition(currentType, selectedPlanType)) {
    return false;
  }
  if (
    currentType === OrganizationType.REHEARSAL_STUDIO &&
    selectedPlanType === OrganizationType.MUSIC_SCHOOL
  ) {
    return false;
  }
  return true;
}

/** True when selecting this plan would move an existing non-studio org to REHEARSAL_STUDIO. */
export function isRehearsalStudioPlanTransition(
  currentType: OrganizationType | null | undefined,
  selectedPlanType: OrganizationType,
): boolean {
  return (
    selectedPlanType === OrganizationType.REHEARSAL_STUDIO &&
    isOrganizationTypePlanTransition(currentType, selectedPlanType)
  );
}
